import {Signal, signal, WritableSignal} from "@angular/core";
import {CancellationToken, CancellationTokenSource, TimeoutError} from "../../tasks";
import {captureException} from "@sentry/angular";

type MetaOrDefault<TMeta> = TMeta extends undefined ? object : { readonly meta: Readonly<TMeta> };
type WithMeta<T, TMeta> = T & MetaOrDefault<TMeta>;
type IfMeta<T, TMeta> = TMeta extends undefined ? object : T;

export type LoadingState<T, TMeta = undefined> = WithMeta<{
  loading: true;
  value?: undefined;
  error?: undefined;
} | {
  loading: false;
  value: T;
  error?: undefined;
} | {
  loading: false;
  error: Error;
  value?: undefined;
}, TMeta>;

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface AsyncSignalOptions {
}

export type AsyncSignalUpdateFn<T, TMeta> = (value: T | undefined, additional: WithMeta<{
  cancellationToken: CancellationToken
}, TMeta>) => Promise<T>;

export type AsyncSignal<T, TMeta = undefined> = Signal<LoadingState<T, TMeta>> & IfMeta<{
  meta(): Readonly<TMeta>;
}, TMeta>

export type AsyncWritableSignal<T, TMeta = undefined> = AsyncSignal<T, TMeta> & {
  update(update: AsyncSignalUpdateFn<T, TMeta>, timeoutMs?: number): void;
  setSuccess(value: T): void;
  setError(error: Error): void;
  setLoading(): void;
  asReadonly(): AsyncSignal<T>;
}


export function asyncSignal<T>(options: AsyncSignalOptions & { initialState: T; }): AsyncWritableSignal<T>;
export function asyncSignal<T>(value: Promise<T> | (() => Promise<T>), options?: AsyncSignalOptions | {
  initialState?: T;
}): AsyncWritableSignal<T>;
export function asyncSignal<T, TMeta>(value: Promise<T> | (() => Promise<T>), options: WithMeta<AsyncSignalOptions | {
  initialState?: T;
}, TMeta>): AsyncWritableSignal<T, TMeta>;
export function asyncSignal<T, TMeta>(defaultedOptions: WithMeta<AsyncSignalOptions & {
  initialState: T;
}, TMeta>): AsyncWritableSignal<T, TMeta>;
export function asyncSignal<T, TMeta = undefined>(valueOrDefaultWithOptions: Promise<T> | (() => Promise<T>) | WithMeta<AsyncSignalOptions & {
  initialState: T;
}, TMeta>, options?: WithMeta<AsyncSignalOptions, TMeta>): AsyncWritableSignal<T, TMeta> {

  let value: Promise<T> | (() => Promise<T>);
  if ('initialState' in valueOrDefaultWithOptions) {
    options = valueOrDefaultWithOptions as WithMeta<AsyncSignalOptions, TMeta>;
    value = Promise.resolve(valueOrDefaultWithOptions.initialState);
  } else {
    value = valueOrDefaultWithOptions as Promise<T> | (() => Promise<T>);
    if (!(value instanceof Promise) && typeof value !== 'function') {
      throw new Error('Invalid value');
    }
  }

  options ??= {} as WithMeta<AsyncSignalOptions, TMeta>;
  let meta: TMeta | undefined;
  if ('meta' in options) {
    meta = options.meta;
  }
  let backingSig: WritableSignal<LoadingState<T, TMeta>>;

  const applyMeta = <TVal>(value: TVal): WithMeta<TVal, TMeta> => {
    if ('meta' in options) {
      (value as { meta: unknown }).meta = options.meta;
    }
    return value as WithMeta<TVal, TMeta>;
  }


  if ('initialState' in options) {
    backingSig = signal<LoadingState<T>>(applyMeta({
      loading: false,
      value: options.initialState as T,
      error: undefined
    }));
  } else {
    backingSig = signal<LoadingState<T>>(applyMeta({loading: true}));
  }
  const sig = backingSig as unknown as AsyncWritableSignal<T, TMeta>;

  let updateStartedAt: number | undefined;
  const setSuccess = (value: T) => {
    backingSig.set(applyMeta({loading: false, value}));
  };
  const setError = (error: Error) => {
    backingSig.set(applyMeta({loading: false, error}));
  };
  const setLoading = () => {
    backingSig.set(applyMeta({loading: true}));
  };

  let lastCancellation: CancellationTokenSource | undefined;


  const update = async (update: AsyncSignalUpdateFn<T, TMeta>, timeoutMs?: number) => {
    const startedAt = Date.now();
    updateStartedAt = startedAt;
    lastCancellation?.cancel();
    const updateCancelSource = new CancellationTokenSource();
    lastCancellation = updateCancelSource;
    const isCurrentUpdate = () => updateStartedAt === startedAt;
    setLoading();

    let resolved = false;
    // `isCurrentUpdate()` only flips when a *new* update starts, so without this flag a slow
    // operation that eventually resolves would overwrite the displayed TimeoutError and flip the
    // value back to valid. Neither `createImageBitmap` nor a WASM decode is interruptible, so this
    // is reachable whenever the timeout fires.
    let timedOut = false;
    if (timeoutMs) {
      setTimeout(() => {
        if (!resolved && isCurrentUpdate()) {
          timedOut = true;
          setError(new TimeoutError(timeoutMs));
          updateCancelSource.cancel();
        }
      }, timeoutMs);
    }
    try {

      const value = await update(backingSig().value, applyMeta({
        cancellationToken: updateCancelSource.token
      }));
      resolved = true;
      // if another update has started, ignore this one
      if (!isCurrentUpdate()) {
        console.debug('resolved but not current', {updateStartedAt, startedAt});
        return;
      }
      if (timedOut) {
        console.debug('resolved after timing out', {updateStartedAt, startedAt, timeoutMs});
        return;
      }

      setSuccess(value);
    } catch (error) {
      resolved = true;
      // if another update has started, ignore this one
      if (!isCurrentUpdate()) {
        console.debug('rejected but not current', {updateStartedAt, startedAt, error});
        return;
      }
      if (timedOut) {
        // The timeout already cancelled this operation; the rejection it produced is a consequence
        // of that, not a separate failure worth reporting or displaying over it.
        console.debug('rejected after timing out', {updateStartedAt, startedAt, timeoutMs, error});
        return;
      }

      console.error(error);
      captureException(error);
      setError(error instanceof Error ? error : new Error(`Unknown error: ${error}`));
    }
  }

  const getMeta = () => meta as Readonly<TMeta>;

  Object.assign(backingSig, {meta: getMeta});

  Object.assign(sig, {setSuccess, setError, setLoading, update});

  (async () => {
    try {
      if (!value) {
        return;
      }
      setSuccess(typeof value === 'function' ? await value() : await value);
    } catch (error) {
      if (error instanceof Error) {
        setError(error);
        return;
      }
      setError(new Error(`Unknown error: ${error}`));
    }
  })();

  return sig;
}
